import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  PublicUser,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
} from '../types/auth';
import { AuthApi } from '../services/authApi';

interface AuthState {
  user: PublicUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  redirectAfterLogin: string | null;
}

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  loginWithGoogle: (idToken: string) => Promise<LoginResponse>;
  completePhone: (phone: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  initialize: () => Promise<void>;
  setRedirectAfterLogin: (url: string | null) => void;
  clearRedirectAfterLogin: () => void;
  getAndClearRedirectUrl: () => string | null;
  /** Cập nhật một phần profile trong store (sau lưu HĐ cá nhân, …) */
  patchUser: (partial: Partial<PublicUser>) => void;
}

interface AuthStore extends AuthState, AuthActions {}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  redirectAfterLogin: null,
};

function applyLoginResponse(
  set: (partial: Partial<AuthState>) => void,
  response: LoginResponse
) {
  if (response.data.accessToken) {
    localStorage.setItem('accessToken', response.data.accessToken);
  }
  if (response.data.refreshToken) {
    localStorage.setItem('refreshToken', response.data.refreshToken);
  }
  set({
    user: response.data.user,
    accessToken: response.data.accessToken,
    refreshToken: response.data.refreshToken,
    isAuthenticated: true,
    isLoading: false,
    error: null,
  });
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: async (credentials: LoginRequest) => {
        try {
          set({ isLoading: true, error: null });

          const response: LoginResponse = await AuthApi.login(credentials);

          if (response.success && response.data) {
            applyLoginResponse(set, response);
            return response;
          }
          throw new Error(response.message || 'Login failed');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      loginWithGoogle: async (idToken: string) => {
        try {
          set({ isLoading: true, error: null });
          const response = await AuthApi.loginWithGoogle({ idToken });
          if (response.success && response.data) {
            applyLoginResponse(set, response);
            return response;
          }
          throw new Error(response.message || 'Google login failed');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Google login failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      completePhone: async (phone: string) => {
        try {
          set({ isLoading: true, error: null });
          const response = await AuthApi.completePhone(phone);
          if (response.success && response.data?.user) {
            set({
              user: response.data.user,
              isLoading: false,
              error: null,
            });
            return;
          }
          throw new Error(response.message || 'Failed to update phone');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update phone';
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      logout: async () => {
        try {
          const { refreshToken } = get();
          if (refreshToken) {
            await AuthApi.logout(refreshToken);
          }
        } catch (error) {
          console.error('Logout API error:', error);
        } finally {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          set({
            ...initialState,
          });
        }
      },

      refreshAccessToken: async () => {
        try {
          const { refreshToken } = get();

          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const response: RefreshTokenResponse = await AuthApi.refreshToken(refreshToken);

          if (response.success && response.data) {
            if (response.data.accessToken) {
              localStorage.setItem('accessToken', response.data.accessToken);
            }
            if (response.data.refreshToken) {
              localStorage.setItem('refreshToken', response.data.refreshToken);
            }

            set({
              user: response.data.user,
              accessToken: response.data.accessToken,
              refreshToken: response.data.refreshToken,
              isAuthenticated: true,
              error: null,
            });
          } else {
            throw new Error(response.message || 'Token refresh failed');
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          set({
            ...initialState,
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      initialize: async () => {
        const { refreshToken, accessToken } = get();

        if (refreshToken && !accessToken) {
          try {
            await get().refreshAccessToken();
          } catch (error) {
            console.error('Failed to initialize auth state:', error);
            set({ ...initialState });
          }
        } else if (accessToken && refreshToken) {
          set({ isAuthenticated: true });
        }
      },

      setRedirectAfterLogin: (url: string | null) => {
        set({ redirectAfterLogin: url });
      },

      clearRedirectAfterLogin: () => {
        set({ redirectAfterLogin: null });
      },

      getAndClearRedirectUrl: () => {
        const { redirectAfterLogin } = get();
        set({ redirectAfterLogin: null });
        return redirectAfterLogin;
      },

      patchUser: (partial) => {
        const { user } = get();
        if (!user) return;
        set({ user: { ...user, ...partial } });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        redirectAfterLogin: state.redirectAfterLogin,
      }),
    }
  )
);

// Helper hooks
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
